import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Info,
  X,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  RefreshCw,
  Landmark,
  CreditCard,
  Building2,
  TrendingUp,
  Receipt,
  FileSpreadsheet,
  ArrowRight,
  UserCheck,
  UserX,
  Wallet,
  MapPin,
  Plane
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGES = {
  'Paid': { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'Pay Pending': { label: 'Pay Pending', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  'Approved': { label: 'Approved', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  'Pending Review': { label: 'Pending Review', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  'Rejected': { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const CLAIM_TYPE_BADGES = {
  'Local Conveyance': { cls: 'bg-teal-50 text-teal-700 border-teal-200', icon: MapPin },
  'Travel Advance': { cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Wallet },
  'Self Booking Reimbursement': { cls: 'bg-purple-50 text-purple-700 border-purple-200', icon: Receipt },
  'Company Booked Travel': { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Plane },
};

const TravelConveyanceLedger = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState({
    total_claims: 0,
    total_claimed: 0,
    total_approved: 0,
    total_rejected: 0,
    total_paid_company: 0,
    total_pending_payment: 0,
    employee_paid_amount: 0,
    company_paid_amount: 0,
    company_payable_employee: 0,
    employee_payable_company: 0,
    outstanding_balance: 0
  });

  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Filters State
  const [companyId, setCompanyId] = useState('all');
  const [branchName, setBranchName] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [department, setDepartment] = useState('all');
  const [claimType, setClaimType] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [travelMode, setTravelMode] = useState('all');
  const [whoPaid, setWhoPaid] = useState('all');
  const [paymentResponsibility, setPaymentResponsibility] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [search, setSearch] = useState('');

  // Selected Detail Modal State
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Fetch Companies & Employees list for dropdown filters
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [compRes, empRes] = await Promise.all([
          axios.get(`${API_URL}/companies/public`, { withCredentials: true }).catch(() => axios.get(`${API_URL}/companies`, { withCredentials: true })),
          axios.get(`${API_URL}/employees/minimal`, { withCredentials: true })
        ]);

        if (compRes.data && Array.isArray(compRes.data)) {
          setCompanies(compRes.data);
        } else if (compRes.data?.data && Array.isArray(compRes.data.data)) {
          setCompanies(compRes.data.data);
        }

        if (empRes.data && Array.isArray(empRes.data)) {
          setEmployees(empRes.data);
        } else if (empRes.data?.data && Array.isArray(empRes.data.data)) {
          setEmployees(empRes.data.data);
        }
      } catch (err) {
        console.error('Failed to load masters:', err);
      }
    };
    fetchMasters();
  }, []);

  // Fetch Ledger Data
  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyId && companyId !== 'all') params.append('company_id', companyId);
      if (branchName && branchName !== 'all') params.append('branch_name', branchName);
      if (employeeId && employeeId !== 'all') params.append('employee_id', employeeId);
      if (department && department !== 'all') params.append('department', department);
      if (claimType && claimType !== 'all') params.append('claim_type', claimType);
      if (paymentStatus && paymentStatus !== 'all') params.append('payment_status', paymentStatus);
      if (travelMode && travelMode !== 'all') params.append('travel_mode', travelMode);
      if (whoPaid && whoPaid !== 'all') params.append('who_paid', whoPaid);
      if (paymentResponsibility && paymentResponsibility !== 'all') params.append('payment_responsibility', paymentResponsibility);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (approvalDate) params.append('approval_date', approvalDate);
      if (paymentDate) params.append('payment_date', paymentDate);
      if (search) params.append('search', search);

      const res = await axios.get(`${API_URL}/travel-desk/finance/ledger?${params.toString()}`);
      if (res.data && res.data.success) {
        setLedger(res.data.ledger || []);
        setSummary(res.data.summary || {});

        // Derive branches list if needed
        const uniqueBranches = Array.from(new Set((res.data.ledger || []).map(i => i.branch_name).filter(Boolean)));
        if (uniqueBranches.length > 0) {
          setBranches(uniqueBranches);
        }
      }
    } catch (err) {
      console.error('Failed to fetch ledger:', err);
      toast.error('Failed to load Travel & Conveyance Ledger data');
    } finally {
      setLoading(false);
    }
  }, [companyId, branchName, employeeId, department, claimType, paymentStatus, travelMode, whoPaid, paymentResponsibility, fromDate, toDate, approvalDate, paymentDate, search]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Export Excel
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (companyId && companyId !== 'all') params.append('company_id', companyId);
      if (branchName && branchName !== 'all') params.append('branch_name', branchName);
      if (employeeId && employeeId !== 'all') params.append('employee_id', employeeId);
      if (department && department !== 'all') params.append('department', department);
      if (claimType && claimType !== 'all') params.append('claim_type', claimType);
      if (paymentStatus && paymentStatus !== 'all') params.append('payment_status', paymentStatus);
      if (travelMode && travelMode !== 'all') params.append('travel_mode', travelMode);
      if (whoPaid && whoPaid !== 'all') params.append('who_paid', whoPaid);
      if (paymentResponsibility && paymentResponsibility !== 'all') params.append('payment_responsibility', paymentResponsibility);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (approvalDate) params.append('approval_date', approvalDate);
      if (paymentDate) params.append('payment_date', paymentDate);
      if (search) params.append('search', search);

      const url = `${API_URL}/travel-desk/finance/ledger/export?${params.toString()}`;
      window.open(url, '_blank');
      toast.success('Excel export started successfully');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error('Failed to export ledger to Excel');
    } finally {
      setExporting(false);
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setCompanyId('all');
    setBranchName('all');
    setEmployeeId('all');
    setDepartment('all');
    setClaimType('all');
    setPaymentStatus('all');
    setTravelMode('all');
    setWhoPaid('all');
    setPaymentResponsibility('all');
    setFromDate('');
    setToDate('');
    setApprovalDate('');
    setPaymentDate('');
    setSearch('');
  };

  const selectedEmpObj = employees.find(e => strEqual(e._id || e.id || e.employee_id, employeeId));

  function strEqual(a, b) {
    return String(a || '').toLowerCase() === String(b || '').toLowerCase();
  }

  const formatCurrency = (val) => {
    const n = Number(val) || 0;
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  };

  return (
    <MainLayout title="Travel & Conveyance Ledger">
      <div className="p-4 md:p-6 space-y-6 bg-slate-50 min-h-screen">
        
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-tr from-blue-600 to-indigo-600 text-white rounded-xl shadow-md">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Travel & Conveyance Ledger</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Unified financial ledger for employee claims, company payments, outstanding balances & complete audit history.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLedger}
              disabled={loading}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm">
              <Filter className="w-4 h-4 text-blue-600" />
              <span>Multi-Criteria Ledger Filters</span>
            </div>
            <button
              onClick={handleResetFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
            >
              Reset Filters
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {/* Search */}
            <div className="col-span-1 lg:col-span-2 relative">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Search Keywords</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Claim ID / Emp Code / Name / Purpose / UTR..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Company</label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Companies</option>
                {companies.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.company_name || c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Branch */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Branch</label>
              <select
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Branches</option>
                {branches.map((b, idx) => (
                  <option key={idx} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Employee</label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Employees</option>
                {employees.map((e) => (
                  <option key={e._id || e.id || e.employee_id} value={e._id || e.id || e.employee_id}>
                    {e.name || e.employee_name || e.full_name} ({e.employee_code || e.code || 'EMP'})
                  </option>
                ))}
              </select>
            </div>

            {/* Claim Type */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Claim Type</label>
              <select
                value={claimType}
                onChange={(e) => setClaimType(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Claim Types</option>
                <option value="local_conveyance">Local Conveyance</option>
                <option value="travel_advance">Travel Advance</option>
                <option value="self_booking">Self Booking Reimbursement</option>
                <option value="company_booked">Company Booked Travel</option>
              </select>
            </div>

            {/* Payment Status */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Payment Status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Payment Statuses</option>
                <option value="pending_review">Pending Review</option>
                <option value="pay_pending">Pay Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid / Disbursed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Travel Mode */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Travel Mode</label>
              <select
                value={travelMode}
                onChange={(e) => setTravelMode(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Travel Modes</option>
                <option value="cab">Cab / Taxi</option>
                <option value="flight">Flight</option>
                <option value="train">Train</option>
                <option value="auto">Auto / Rickshaw</option>
                <option value="own">Own Vehicle</option>
              </select>
            </div>

            {/* Who Paid */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Who Paid Initially</label>
              <select
                value={whoPaid}
                onChange={(e) => setWhoPaid(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="employee">Employee</option>
                <option value="company">Company</option>
              </select>
            </div>

            {/* Payment Responsibility */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Responsibility</label>
              <select
                value={paymentResponsibility}
                onChange={(e) => setPaymentResponsibility(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="company">Company</option>
                <option value="client_company">Client Company</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Travel Date From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Travel Date To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Employee Profile Header if filtered by specific employee */}
        {employeeId !== 'all' && (
          <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-5 rounded-2xl shadow-md flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center text-blue-300 font-bold text-lg">
                {(selectedEmpObj?.name || 'EMP').charAt(0)}
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-500/30 text-blue-200 border border-blue-400/30">
                  Employee Ledger Mode
                </span>
                <h2 className="text-lg font-bold mt-0.5">
                  {selectedEmpObj?.name || selectedEmpObj?.employee_name || 'Selected Employee'}
                </h2>
                <div className="text-xs text-slate-300 flex items-center gap-3 mt-0.5">
                  <span>Code: <b>{selectedEmpObj?.employee_code || selectedEmpObj?.code || 'N/A'}</b></span>
                  <span>•</span>
                  <span>Dept: <b>{selectedEmpObj?.department_name || 'General'}</b></span>
                </div>
              </div>
            </div>
            <div className="text-right border-l border-slate-700/60 pl-6">
              <div className="text-xs text-slate-400">Net Outstanding Balance</div>
              <div className={`text-xl font-extrabold ${summary.outstanding_balance > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {formatCurrency(summary.outstanding_balance)}
              </div>
            </div>
          </div>
        )}

        {/* 11 KPI Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* 1. Total Claims */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">Total Claims</div>
            <div className="text-xl font-bold text-slate-900 mt-1">{summary.total_claims || 0}</div>
            <div className="text-[10px] text-slate-400 mt-1">Filtered Transactions</div>
          </div>

          {/* 2. Total Claimed */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">Total Claimed</div>
            <div className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(summary.total_claimed)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Gross Requested</div>
          </div>

          {/* 3. Total Approved */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-emerald-600 text-[11px] font-semibold uppercase tracking-wider">Total Approved</div>
            <div className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(summary.total_approved)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Eligible / Approved</div>
          </div>

          {/* 4. Total Rejected */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-rose-600 text-[11px] font-semibold uppercase tracking-wider">Total Rejected</div>
            <div className="text-lg font-bold text-rose-700 mt-1">{formatCurrency(summary.total_rejected)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Disallowed Claims</div>
          </div>

          {/* 5. Total Paid by Company */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-blue-600 text-[11px] font-semibold uppercase tracking-wider">Paid by Company</div>
            <div className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(summary.total_paid_company)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Disbursed Funds</div>
          </div>

          {/* 6. Pending Payment */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-amber-600 text-[11px] font-semibold uppercase tracking-wider">Pending Payment</div>
            <div className="text-lg font-bold text-amber-700 mt-1">{formatCurrency(summary.total_pending_payment)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Finance Queue</div>
          </div>

          {/* 7. Employee Paid / Self Booking */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-purple-600 text-[11px] font-semibold uppercase tracking-wider">Employee Paid</div>
            <div className="text-lg font-bold text-purple-700 mt-1">{formatCurrency(summary.employee_paid_amount)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Self Booking / Out-of-pocket</div>
          </div>

          {/* 8. Company Paid Amount */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-sky-600 text-[11px] font-semibold uppercase tracking-wider">Company Paid</div>
            <div className="text-lg font-bold text-sky-700 mt-1">{formatCurrency(summary.company_paid_amount)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Pre-Travel Advances</div>
          </div>

          {/* 9. Company Payable to Employee */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-indigo-600 text-[11px] font-semibold uppercase tracking-wider">Company Payable</div>
            <div className="text-lg font-bold text-indigo-700 mt-1">{formatCurrency(summary.company_payable_employee)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Owed to Employee</div>
          </div>

          {/* 10. Employee Payable to Company */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-orange-600 text-[11px] font-semibold uppercase tracking-wider">Employee Payable</div>
            <div className="text-lg font-bold text-orange-700 mt-1">{formatCurrency(summary.employee_payable_company)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Owed to Company</div>
          </div>

          {/* 11. Outstanding Balance */}
          <div className="bg-slate-900 text-white p-4 rounded-xl border border-slate-800 shadow-sm col-span-2 sm:col-span-2 lg:col-span-2">
            <div className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider">Net Outstanding Balance</div>
            <div className={`text-xl font-extrabold mt-1 ${summary.outstanding_balance > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
              {formatCurrency(summary.outstanding_balance)}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Net Reimbursement Balance</div>
          </div>
        </div>

        {/* 32-Column Full Transaction Ledger Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-600" />
              <span>Complete Transaction History Ledger ({ledger.length} records)</span>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
              <p className="text-xs font-medium">Fetching Travel & Conveyance Ledger records...</p>
            </div>
          ) : ledger.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">No Ledger Transactions Found</p>
              <p className="text-xs text-slate-400 mt-1">Try adjusting your filters or search keywords.</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="w-full text-left text-xs border-collapse min-w-[2400px]">
                <thead className="bg-slate-950 text-slate-200 font-semibold uppercase text-[10px] tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-800 text-center">Action</th>
                    <th className="p-3 border-b border-slate-800">Claim / Txn ID</th>
                    <th className="p-3 border-b border-slate-800">Emp Code</th>
                    <th className="p-3 border-b border-slate-800">Employee Name</th>
                    <th className="p-3 border-b border-slate-800">Department</th>
                    <th className="p-3 border-b border-slate-800">Company / Branch</th>
                    <th className="p-3 border-b border-slate-800">Claim Type</th>
                    <th className="p-3 border-b border-slate-800">Travel Purpose</th>
                    <th className="p-3 border-b border-slate-800">From → To</th>
                    <th className="p-3 border-b border-slate-800">Travel Date</th>
                    <th className="p-3 border-b border-slate-800">Return Date</th>
                    <th className="p-3 border-b border-slate-800">Travel Mode</th>
                    <th className="p-3 border-b border-slate-800 text-right">Claimed (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Approved (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Rejected (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Payable (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Paid (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Pending (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-center">Payment Status</th>
                    <th className="p-3 border-b border-slate-800">Payment Date</th>
                    <th className="p-3 border-b border-slate-800">Approval Date</th>
                    <th className="p-3 border-b border-slate-800">Approved By</th>
                    <th className="p-3 border-b border-slate-800">Processed By</th>
                    <th className="p-3 border-b border-slate-800">UTR / Ref No</th>
                    <th className="p-3 border-b border-slate-800">Method</th>
                    <th className="p-3 border-b border-slate-800 text-right">Emp Paid (₹)</th>
                    <th className="p-3 border-b border-slate-800">Who Paid</th>
                    <th className="p-3 border-b border-slate-800">Responsibility</th>
                    <th className="p-3 border-b border-slate-800 text-right">Co. Payable (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Emp Payable (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Balance (₹)</th>
                    <th className="p-3 border-b border-slate-800">Remarks</th>
                    <th className="p-3 border-b border-slate-800">Created / Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-normal text-slate-700 bg-white">
                  {ledger.map((item, idx) => {
                    const stBadge = STATUS_BADGES[item.payment_status] || STATUS_BADGES['Pending Review'];
                    const ctBadge = CLAIM_TYPE_BADGES[item.claim_type] || CLAIM_TYPE_BADGES['Travel Advance'];
                    const CtIcon = ctBadge.icon;

                    return (
                      <tr key={item.id || idx} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          <button
                            onClick={() => setSelectedRecord(item)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Transaction Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono font-semibold text-blue-700">
                          {item.transaction_id}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-600">
                          {item.employee_code}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-medium text-slate-900">
                          {item.employee_name}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600">
                          {item.department}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600 max-w-[180px] truncate" title={item.company_branch}>
                          {item.company_branch}
                        </td>
                        <td className="p-2.5 border-b border-slate-100">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ctBadge.cls}`}>
                            <CtIcon className="w-3 h-3" />
                            {item.claim_type}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-800 max-w-[200px] truncate" title={item.purpose}>
                          {item.purpose}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600 font-medium">
                          {item.from_to}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-600">
                          {item.travel_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-500">
                          {item.return_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-700">
                          {item.travel_mode}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-medium text-slate-900">
                          {formatCurrency(item.claimed_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-medium text-emerald-700">
                          {formatCurrency(item.approved_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-medium text-rose-600">
                          {formatCurrency(item.rejected_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-semibold text-slate-900">
                          {formatCurrency(item.payable_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-bold text-blue-700">
                          {formatCurrency(item.amount_paid)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-semibold text-amber-700">
                          {formatCurrency(item.pending_payment_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${stBadge.cls}`}>
                            {stBadge.label}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-600">
                          {item.payment_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-600">
                          {item.approval_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600">
                          {item.approved_by}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600">
                          {item.payment_processed_by}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-700 font-medium">
                          {item.utr_reference}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-600">
                          {item.payment_method}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right text-purple-700 font-medium">
                          {formatCurrency(item.employee_paid_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${item.who_paid === 'Employee' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {item.who_paid}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-700">
                          {item.payment_responsibility}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-semibold text-indigo-700">
                          {formatCurrency(item.company_has_to_pay)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-semibold text-orange-700">
                          {formatCurrency(item.employee_has_to_pay)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-extrabold text-slate-900">
                          {formatCurrency(item.outstanding_balance)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-500 max-w-[180px] truncate" title={item.remarks}>
                          {item.remarks || '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-400 text-[10px]">
                          {item.created_updated_date}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Detail View Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-base">Transaction Details</h3>
                  <p className="text-xs text-slate-500 font-mono">{selectedRecord.transaction_id}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 text-xs text-slate-700">
              {/* Employee Header */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Employee</span>
                  <span className="font-bold text-slate-900">{selectedRecord.employee_name}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Code</span>
                  <span className="font-mono font-medium text-slate-800">{selectedRecord.employee_code}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Department</span>
                  <span className="font-medium text-slate-800">{selectedRecord.department}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Company / Branch</span>
                  <span className="font-medium text-slate-800">{selectedRecord.company_branch}</span>
                </div>
              </div>

              {/* Claim & Travel Details */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-blue-600">Claim & Travel Info</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Claim Type</span>
                    <span className="font-semibold text-slate-900">{selectedRecord.claim_type}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Purpose</span>
                    <span className="font-medium text-slate-800">{selectedRecord.purpose}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">From → To</span>
                    <span className="font-medium text-slate-800">{selectedRecord.from_to}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Travel Date</span>
                    <span className="font-mono text-slate-800">{selectedRecord.travel_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Return Date</span>
                    <span className="font-mono text-slate-800">{selectedRecord.return_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Travel Mode</span>
                    <span className="font-medium text-slate-800">{selectedRecord.travel_mode}</span>
                  </div>
                </div>
              </div>

              {/* Financial Amounts Breakdown */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-emerald-600">Financial Ledger Breakdown</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-500 text-[10px] block">Claimed Amount</span>
                    <span className="font-bold text-slate-900 text-sm">{formatCurrency(selectedRecord.claimed_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Approved Amount</span>
                    <span className="font-bold text-emerald-700 text-sm">{formatCurrency(selectedRecord.approved_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Rejected Amount</span>
                    <span className="font-bold text-rose-600 text-sm">{formatCurrency(selectedRecord.rejected_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Payable Amount</span>
                    <span className="font-bold text-slate-900 text-sm">{formatCurrency(selectedRecord.payable_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Amount Already Paid</span>
                    <span className="font-bold text-blue-700 text-sm">{formatCurrency(selectedRecord.amount_paid)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Pending Payment</span>
                    <span className="font-bold text-amber-700 text-sm">{formatCurrency(selectedRecord.pending_payment_amount)}</span>
                  </div>
                </div>
              </div>

              {/* Approval & Disbursement Info */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-indigo-600">Approval & Finance Audit</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Payment Status</span>
                    <span className="font-semibold text-slate-900">{selectedRecord.payment_status}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Approval Date</span>
                    <span className="font-mono text-slate-800">{selectedRecord.approval_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Approved By</span>
                    <span className="font-medium text-slate-800">{selectedRecord.approved_by}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Payment Processed By</span>
                    <span className="font-medium text-slate-800">{selectedRecord.payment_processed_by}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">UTR / Transaction Ref</span>
                    <span className="font-mono font-semibold text-slate-900">{selectedRecord.utr_reference}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Payment Method</span>
                    <span className="font-medium text-slate-800">{selectedRecord.payment_method}</span>
                  </div>
                </div>
              </div>

              {/* Balances Summary */}
              <div className="bg-slate-900 text-white p-4 rounded-xl flex items-center justify-between">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Net Outstanding Balance</div>
                  <div className="text-lg font-extrabold text-amber-400">{formatCurrency(selectedRecord.outstanding_balance)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-400 uppercase">Responsibility</div>
                  <div className="font-bold">{selectedRecord.payment_responsibility}</div>
                </div>
              </div>

              {selectedRecord.remarks && (
                <div>
                  <span className="text-slate-400 text-[10px] block font-semibold uppercase">Remarks / Audit Note</span>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-700 italic">
                    {selectedRecord.remarks}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setSelectedRecord(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default TravelConveyanceLedger;
